<template>
    <div v-on-click-outside="onClickOutsideHandler" class="relative w-full">
        <AdminSearch
            ref="searchInput"
            v-model="search"
            type="search"
            :placeholder="lang('search::search.placeholder')"
            @focus="submenu = true"
        />

        <kbd
            class="absolute px-2 rounded-md py-0.5 text-xs -translate-y-1/2 border shadow-sm top-1/2 right-2 font-bold cursor-pointer border-stone-300 dark:border-zinc-700 hover:border-stone-400 dark:hover:border-zinc-600 dark:text-zinc-400"
            @click="focusSearch"
            >/</kbd
        >

        <div
            v-if="(hasSearchResults && submenu) || (hasSearchQuery && submenu)"
            class="absolute inset-x-0 z-10 bg-white dark:bg-zinc-900 border dark:border-zinc-800 shadow-sm top-full max-h-[50vh] overflow-y-auto"
        >
            <div v-if="hasSearchResults && submenu">
                <div v-for="group in searchResults" :key="group.title">
                    <div
                        v-if="group.results.length > 0"
                        class="px-2 py-1 text-sm font-bold bg-stone-100 dark:bg-zinc-800"
                    >
                        {{ group.title }}
                    </div>
                    <div class="divide-y dark:divide-zinc-800">
                        <inertia-link
                            v-for="result in group.results"
                            :key="result.id"
                            :href="result.url"
                            class="block px-2 py-1 space-y-1 text-sm hover:bg-stone-50 dark:hover:bg-zinc-950"
                        >
                            <div v-if="result.title">
                                {{ result.title }}
                            </div>
                            <div v-if="result.description" class="text-xs opacity-80">
                                {{ result.description }}
                            </div>
                        </inertia-link>
                    </div>
                </div>
            </div>
            <div v-else-if="hasSearchQuery && submenu">
                <p class="px-2 py-1 text-sm text-center">
                    {{ lang('search::search.no_results') }}
                </p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import axios from 'axios';
import { vOnClickOutside } from '@vueuse/components';
import { onKeyStroke } from '@vueuse/core';

/**
 * Search
 */
const search = ref('' as string);
const searchInput = ref(null as HTMLInputElement | null);
const submenu = ref(false);
const searchResults = ref([]);
const hasSearchResults = computed(() => {
    let result = false;

    searchResults.value.forEach((group) => {
        if (group.results.length > 0) {
            result = true;
        }
    });

    return result;
});
const hasSearchQuery = computed(() => search.value.length > 0);

const ignoreElRef = ref();

const onClickOutsideHandler = [
    () => {
        submenu.value = false;
    },
    { ignore: [ignoreElRef] },
];

watch(search, (value) => {
    if (value) {
        axios
            .get(route('search.admin.api.search'), {
                params: {
                    search: search.value,
                },
            })
            .then(({ data }) => {
                if (data.data.length > 0) {
                    submenu.value = true;
                }

                searchResults.value = data.data;
            });
    }
});

const focusSearch = () => {
    searchInput.value?.focus();
};

onKeyStroke('/', (e) => {
    // If currently focused element is not INPUT or TEXTAREA
    if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();

        focusSearch();
    }
});
</script>

<style>
[type='search']::-webkit-search-decoration,
[type='search']::-webkit-search-cancel-button {
    appearance: none;
}
</style>
